import type { RumEvent, RumInitConfiguration } from '@datadog/browser-rum';
import { CONFIG } from '@frameio/config';
import {
  getRecentEvent,
  TELEMETRY_EVENT_ID_HEADER,
  TELEMETRY_EVENT_NAME_HEADER,
} from '../events';
import getHeader from './getHeader';
import isAssetUrl from './isAssetUrl';
import redactGraphQlVariables from './redactGraphQlVariables';
import redactQueryString from './redactQueryString';

const API_URL_REGEX =
  /^https:\/\/api(-pr-\d+)?\.(dev\.|staging\.)?frame\.io(\/|$)/;

/**
 * Get a dictionary of options for the telemetry API
 * https://docs.datadoghq.com/real_user_monitoring/browser/#initialization-parameters
 */
const getRumOptions = (): Partial<RumInitConfiguration> => ({
  /**
   * A list of request origins used to inject tracing headers
   */
  allowedTracingUrls: [
    {
      match: 'https://next.dev.frame.io',
      propagatorTypes: ['tracecontext', 'datadog'],
    },
    {
      match: 'https://next.frame.io',
      propagatorTypes: ['tracecontext', 'datadog'],
    },
    {
      match: 'https://next.staging.frame.io',
      propagatorTypes: ['tracecontext', 'datadog'],
    },
    {
      match: 'https://integrations.dev.frame.io',
      propagatorTypes: ['tracecontext', 'datadog'],
    },
    {
      match: 'https://integrations.staging.frame.io',
      propagatorTypes: ['tracecontext', 'datadog'],
    },
    {
      match: 'https://integrations.frame.io',
      propagatorTypes: ['tracecontext', 'datadog'],
    },
    {
      match: API_URL_REGEX,
      propagatorTypes: ['tracecontext', 'datadog'],
    },
    {
      match: /^https:\/\/[\w-]+\.vercel\.dev\.frame\.io(\/|$)/,
      propagatorTypes: ['tracecontext', 'datadog'],
    },

    // See VIEW-4063 & web-app#11994
    // `https://assets.dev.frame.io`,
    // `https://assets.frame.io`,
    // `https://assets.staging.frame.io`,
  ],
  allowedGraphQlUrls: [
    {
      match: API_URL_REGEX,
      trackPayload: false, // set true to include the query text (max 32 KB)
      trackResponseErrors: true, // captures GraphQL errors from 200 responses
    },
  ],
  /**
   * The `beforeSend` callback function gives you access to every event collected by the RUM SDK before they are sent to Datadog. Intercepting the RUM events allows you to:
   * https://docs.datadoghq.com/real_user_monitoring/guide/enrich-and-control-rum-data/?tab=event#event-and-context-structure
   * Enrich your RUM events with additional context attributes
   * Modify your RUM events to modify their content, or redact sensitive sequences (see list of editable properties)
   * Discard selected RUM events
   */
  // eslint-disable-next-line consistent-return
  beforeSend(evt: RumEvent, ctx: any) {
    /* eslint-disable no-param-reassign */

    /**
     * let's discard this seemingly benign ResizeObserver error from being sent
     *
     * https://stackoverflow.com/questions/49384120/resizeobserver-loop-limit-exceeded/50387233#50387233
     * https://docs.datadoghq.com/real_user_monitoring/guide/enrich-and-control-rum-data/?tab=event#discard-a-frontend-error
     */
    if (
      evt.type === 'error' &&
      (/ResizeObserver loop (limit exceeded|completed with undelivered notifications)/.test(
        String(evt.error?.stack)
      ) ||
        evt.error?.stack === 'Error: Script error.\n  at undefined @ ') // Safari has less helpful messaging
    ) {
      return false;
    }

    if (evt.type === `resource` && isAssetUrl(evt.resource.url)) {
      evt.resource.url = redactQueryString(evt.resource.url);
    } else if (
      evt.type === 'resource' &&
      evt.resource.url === CONFIG.graphqlUrl
    ) {
      const { graphql } = evt.resource;
      if (graphql?.variables !== undefined) {
        graphql.variables = redactGraphQlVariables(graphql.variables);
      }

      const rawHeaders = ctx?.requestInit?.headers;

      // get tracing headers from Apollo and append them to the RUM object
      // as context.
      const telemetryEventName = getHeader(
        rawHeaders,
        TELEMETRY_EVENT_NAME_HEADER
      );
      const telemetryEventId = getHeader(rawHeaders, TELEMETRY_EVENT_ID_HEADER);
      const telemetryEvent = telemetryEventId
        ? getRecentEvent(telemetryEventId)
        : undefined;

      evt.context = { ...evt.context, event_name: telemetryEventName };
      if (telemetryEvent) {
        evt.context.telemetry_ctx = telemetryEvent.getContext();
      }
    } else if (evt.type === `view` && isAssetUrl(evt.view.url)) {
      evt.view.url = redactQueryString(evt.view.url);
    }
    /* eslint-enable no-param-reassign */
  },
});

export default getRumOptions;
